import { useEffect, useState } from "react";
import {
  completeStockTransfer,
  createStockTransfer,
  listInventoryItems,
  listStockTransfers,
  listWarehouses,
  type AdminSession,
  type InventoryItemOption,
  type StockTransferItem,
  type WarehouseItem,
} from "@/lib/api";

type TransferLine = {
  inventoryItemId: string;
  quantity: number;
};

type TransferHeaderForm = {
  fromWarehouseId: string;
  toWarehouseId: string;
};

const emptyLine: TransferLine = { inventoryItemId: "", quantity: 1 };
const emptyHeader: TransferHeaderForm = { fromWarehouseId: "", toWarehouseId: "" };

export default function TransferenciasSection({ session }: { session: AdminSession }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [items, setItems] = useState<InventoryItemOption[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseItem[]>([]);
  const [transfers, setTransfers] = useState<StockTransferItem[]>([]);

  const [header, setHeader] = useState<TransferHeaderForm>(emptyHeader);
  const [lines, setLines] = useState<TransferLine[]>([{ ...emptyLine }]);

  async function load(currentSession: AdminSession): Promise<void> {
    setLoading(true);
    setError("");
    try {
      const [itemData, warehouseData, transferData] = await Promise.all([
        listInventoryItems(currentSession),
        listWarehouses(currentSession),
        listStockTransfers(currentSession),
      ]);
      setItems(itemData);
      setWarehouses(warehouseData);
      setTransfers(transferData);
      setHeader((prev) => {
        const defaultFrom = prev.fromWarehouseId || warehouseData[0]?.id || "";
        const defaultTo = prev.toWarehouseId || warehouseData.find((w) => w.id !== defaultFrom)?.id || warehouseData[0]?.id || "";
        return { fromWarehouseId: defaultFrom, toWarehouseId: defaultTo };
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudieron cargar transferencias");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(session);
  }, [session]);

  function updateLine(index: number, patch: Partial<TransferLine>): void {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  }

  function addLine(): void {
    setLines((prev) => [...prev, { ...emptyLine }]);
  }

  function removeLine(index: number): void {
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  }

  async function handleCreateTransfer(): Promise<void> {
    if (!header.fromWarehouseId || !header.toWarehouseId) {
      setError("Completa bodega de salida y bodega de entrada.");
      return;
    }
    if (header.fromWarehouseId === header.toWarehouseId) {
      setError("Bodega origen y destino no pueden ser iguales.");
      return;
    }
    const validLines = lines.filter((line) => line.inventoryItemId && line.quantity > 0);
    if (validLines.length === 0) {
      setError("Agrega al menos un ítem con cantidad para transferir.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await createStockTransfer(session, {
        fromWarehouseId: header.fromWarehouseId,
        toWarehouseId: header.toWarehouseId,
        items: validLines.map((line) => ({ inventoryItemId: line.inventoryItemId, quantity: Number(line.quantity) })),
      });
      setLines([{ ...emptyLine }]);
      await load(session);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo crear la transferencia");
    } finally {
      setSaving(false);
    }
  }

  async function handleCompleteTransfer(transferId: string): Promise<void> {
    setSaving(true);
    setError("");
    try {
      await completeStockTransfer(session, transferId);
      await load(session);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo completar la transferencia");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {error ? (
        <div className="lg:col-span-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="lg:col-span-1">
        <div className="rounded-xl border border-gray-100 bg-white p-4">
          <h2 className="mb-2 text-sm font-semibold text-gray-700">Nueva Transferencia</h2>
          <div className="space-y-2">
            <select value={header.fromWarehouseId} onChange={(e) => setHeader((p) => ({ ...p, fromWarehouseId: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
              <option value="">Bodega salida</option>
              {warehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.code} · {warehouse.name}</option>)}
            </select>
            <select value={header.toWarehouseId} onChange={(e) => setHeader((p) => ({ ...p, toWarehouseId: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
              <option value="">Bodega entrada</option>
              {warehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.code} · {warehouse.name}</option>)}
            </select>
          </div>

          <div className="mt-4">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase text-gray-500">Detalle</span>
              <button onClick={addLine} className="rounded-lg border border-blue-200 px-2 py-1 text-xs text-blue-700">+ Agregar ítem</button>
            </div>
            <div className="space-y-2">
              {lines.map((line, index) => (
                <div key={index} className="rounded-lg border border-gray-100 p-2">
                  <select value={line.inventoryItemId} onChange={(e) => updateLine(index, { inventoryItemId: e.target.value })} className="mb-2 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs">
                    <option value="">Producto</option>
                    {items.map((item) => <option key={item.id} value={item.id}>{item.sku} · {item.name}</option>)}
                  </select>
                  <div className="flex items-center gap-2">
                    <input type="number" min={1} value={line.quantity} onChange={(e) => updateLine(index, { quantity: Number(e.target.value || 1) })} className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs" placeholder="Cantidad" />
                    <button onClick={() => removeLine(index)} disabled={lines.length === 1} className="shrink-0 rounded-lg border border-red-200 px-2 py-1.5 text-xs text-red-700 disabled:opacity-40">✕</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 flex justify-end">
            <button disabled={saving} onClick={() => void handleCreateTransfer()} className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white">Crear transferencia</button>
          </div>
        </div>
      </div>

      <div className="lg:col-span-2">
        <div className="rounded-xl border border-gray-100 bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">Transferencias entre bodegas</h2>
            <span className="text-xs text-gray-500">{transfers.length} registros</span>
          </div>
          {loading ? (
            <div className="text-sm text-gray-500">Cargando transferencias...</div>
          ) : (
            <div className="space-y-2">
              {transfers.map((transfer) => (
                <div key={transfer.id} className="rounded-lg border border-gray-100 p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">{transfer.id}</div>
                      <div className="text-xs text-gray-500">
                        {transfer.fromWarehouseCode || "-"} · {transfer.fromWarehouseName || "Origen"} → {transfer.toWarehouseCode || "-"} · {transfer.toWarehouseName || "Destino"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">{transfer.requestedAt ? new Date(transfer.requestedAt).toLocaleDateString("es-CL") : "-"}</div>
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs ${transfer.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        {transfer.status === "completed" ? "Completada" : "Solicitada"}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    {transfer.items.length === 0 ? "Sin ítems" : transfer.items.map((item) => `${item.itemSku || "-"} · ${item.itemName || "Producto"} (${item.quantity})`).join(" | ")}
                  </div>
                  {transfer.status !== "completed" ? (
                    <div className="mt-2 flex justify-end">
                      <button disabled={saving} onClick={() => void handleCompleteTransfer(transfer.id)} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs text-white">Completar transferencia</button>
                    </div>
                  ) : null}
                </div>
              ))}
              {transfers.length === 0 ? <div className="text-sm text-gray-500">No hay transferencias registradas.</div> : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
